import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePriceData } from '@/hooks/usePriceData';

// 全局价格监控组件
// 这个组件不渲染任何UI，只负责在后台监控价格
const PriceMonitor: React.FC = () => {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const { priceData } = usePriceData();
  const [settings, setSettings] = useState({
    enabled: false,
    lowThreshold: 0.95,
    highThreshold: 1.05,
    notifications: false,
    silentMode: false,
    alertFrequency: 'high' as 'low' | 'high' | 'custom',
  });
  const [alertState, setAlertState] = useState({
    lastAlertTime: 0,
    hasPermission: false,
    permissionRequested: false,
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const ALERT_COOLDOWN = 5 * 60 * 1000; // 5分钟冷却时间
  const PRICE_CHECK_INTERVAL = 60 * 1000; // 60秒检查间隔

  // 加载设置
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem('dor-price-alert-settings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsed }));
        }
        
        const lastAlert = localStorage.getItem('dor-last-alert-time');
        if (lastAlert) {
          setAlertState(prev => ({ ...prev, lastAlertTime: parseInt(lastAlert) }));
        }

        // 加载权限状态
        const permissionRequested = localStorage.getItem('dor-notification-permission-requested') === 'true';
        const hasPermission = Notification.permission === 'granted';
        setAlertState(prev => ({ 
          ...prev, 
          permissionRequested,
          hasPermission 
        }));
      } catch (error) {
        // Failed to load price alert settings
      }
    };

    loadSettings();
  }, []);

  // 发送浏览器通知
  const sendBrowserNotification = (title: string, body: string, alertType: 'low' | 'high') => {
    if ('Notification' in window && alertState.hasPermission && !settings.silentMode) {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.svg',
        badge: '/favicon.svg',
        tag: 'dor-price-alert',
        requireInteraction: false,
        silent: true,
      });

      notification.onclick = () => {
        window.focus();
        if (alertType === 'low') {
          window.location.href = '/invite';
        } else {
          window.location.href = '/recharge';
        }
        notification.close();
      };

      setTimeout(() => {
        notification.close();
      }, 10000);
    }
  };

  // 发送Toast通知
  const sendToastNotification = (title: string, description: string, status: 'warning' | 'success' | 'info', actionUrl?: string) => {
    toast({
      title,
      description: actionUrl ? `${description} (点击查看详情)` : description,
      status,
      duration: 8000,
      isClosable: true,
      position: 'top-right',
    });
    
    // 如果有actionUrl，添加点击事件监听器
    if (actionUrl) {
      // 这里可以添加自定义的点击处理逻辑
      // 由于toast的onClick属性不存在，我们可以通过其他方式处理
    }
  };

  // 检查价格并发送提醒
  const checkPriceAndAlert = () => {
    if (!settings.enabled || !priceData?.currentPrice) return;

    const currentPrice = priceData.currentPrice;
    const now = Date.now();
    
    const cooldown = settings.alertFrequency === 'low' ? 15 * 60 * 1000 : ALERT_COOLDOWN;
    
    if (now - alertState.lastAlertTime < cooldown) return;

    let shouldAlert = false;
    let alertType: 'low' | 'high' | null = null;

    if (currentPrice < settings.lowThreshold) {
      shouldAlert = true;
      alertType = 'low';
    } else if (currentPrice > settings.highThreshold) {
      shouldAlert = true;
      alertType = 'high';
    }

    if (shouldAlert && alertType) {
      const deviation = alertType === 'low' 
        ? ((settings.lowThreshold - currentPrice) / settings.lowThreshold * 100).toFixed(1)
        : ((currentPrice - settings.highThreshold) / settings.highThreshold * 100).toFixed(1);

      const title = t('priceAlert.title');
      const body = alertType === 'low'
        ? t('priceAlert.lowBody', { 
            threshold: settings.lowThreshold, 
            price: currentPrice.toFixed(3),
            deviation 
          })
        : t('priceAlert.highBody', { 
            threshold: settings.highThreshold, 
            price: currentPrice.toFixed(3),
            deviation 
          });

      sendBrowserNotification(title, body, alertType);

      const actionUrl = alertType === 'low' ? '/invite' : '/recharge';
      sendToastNotification(
        title,
        body,
        alertType === 'low' ? 'warning' : 'success',
        actionUrl
      );

      setAlertState(prev => ({ ...prev, lastAlertTime: now }));
      localStorage.setItem('dor-last-alert-time', now.toString());
    }
  };

  // 启动价格监控
  useEffect(() => {
    if (settings.enabled) {
      checkPriceAndAlert();
      intervalRef.current = setInterval(checkPriceAndAlert, PRICE_CHECK_INTERVAL);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [settings.enabled, priceData?.currentPrice, alertState.lastAlertTime, settings.lowThreshold, settings.highThreshold]);

  // 不渲染任何内容
  return null;
};

export default PriceMonitor; 